import React, { FC } from 'react'
import styled from 'styled-components'
import { Text } from 'ui'
import { SummaryProps } from './types'
import { CuponsFileUploadIcon, LocationIcon, CalendarClockIcon, EditIcon } from '../icons'

const SummaryContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  /* Hide scrollbar */
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`

const SummarySection = styled.div`
  margin-bottom: 24px;
  background: #FFF;
  padding: 12px;
  border-radius: 16px;
  border: 1px solid var(--Neutral-Borders-Default, #D1D1D1);

`

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`

const SectionTitleRow = styled.div`
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  gap: 8px;
`

const EditButton = styled.div`
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  gap: 2px;
  padding: 2px;
  cursor: pointer;
`

const SectionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const FieldRow = styled.div<{ showBorder?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-top: ${props => props.showBorder ? '1px dashed #D1D1D1' : 'none'};
  padding-top: ${props => props.showBorder ? '16px' : '0px'};
  margin-top: ${props => props.showBorder ? '0px' : '0px'};
`

const FieldLabel = styled.div``
const FieldValue = styled.div``

const DiscountTag = styled.div`
  display: flex;
  width: 55px;
  height: 32px;
  padding: 12px 8px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 30px;
  background: var(--Neutral-Surface-Light, #F6F6F6);
  box-sizing: border-box;
`

const PriceRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  justify-content: flex-start;
`

const ThumbnailsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

`

const ThumbnailImage = styled.img`
    width: 86.69px;
    height: 57.79px;
    object-fit: cover;
    border-radius: 8px;
    border: 1px solid var(--Neutral-Borders-Default, #D1D1D1);

`

const CommunitiesContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px;
  direction: rtl; 
`

const CommunityTag = styled.div`
  background-color: #F5F5F5;
  border-radius: 100px;
  padding: 8px 16px;
`

const Summary: FC<SummaryProps> = ({
    title,
    description,
    images,
    discountPercentage,
    price,
    selectedCategory,
    selectedOrganizations,
    organizations,
    startDate,
    endDate,
    formatDate,
    onEditStep,
    ctaType,
    ctaValue,
    couponCode,
}) => {

    // Get selected organization names
    const selectedOrgNames = organizations
        .filter(org => selectedOrganizations.has(org.community.community))
        .map(org => org.community.name);

    // Calculate original price if discount exists
    const discountNum = parseFloat(discountPercentage) || 0;
    const currentPrice = parseFloat(price) || 0;
    const originalPrice = discountNum > 0 ? Math.round(currentPrice / (1 - discountNum / 100)) : currentPrice;

    const benefitDetailsFields = [
        true, // title
        true, // description
        images.length > 0,
        !!discountPercentage,
        !!price,
        !!selectedCategory,
        true, // product link
        ctaType === 'url', // coupon code
    ].filter(Boolean).length;

    const datesFields = [!!startDate, !!endDate].filter(Boolean).length;

    const shouldShowBorder = (fieldIndex: number, totalFields: number) => {
        return fieldIndex > 0 && totalFields > 1;
    };

    let benefitDetailsIndex = 0;
    let datesIndex = 0;

    return (
        <SummaryContainer>
            {/* Benefit Details Section */}
            <SummarySection>
                <SectionHeader>
                    <SectionTitleRow>
                        <Text size="lg" weight="bold" style={{ color: '#272727' }}>
                            פרטי הטבה
                        </Text>
                        <CuponsFileUploadIcon />
                    </SectionTitleRow>
                    <EditButton onClick={() => onEditStep(1)}>
                        <Text size="sm" weight="normal" style={{ color: '#007EFF' }}>
                            עריכה
                        </Text>
                        <EditIcon />
                    </EditButton>
                </SectionHeader>

                <SectionContent>
                    <FieldRow showBorder={shouldShowBorder(benefitDetailsIndex++, benefitDetailsFields)}>
                        <FieldLabel>
                            <Text size="md" weight="bold" style={{ color: '#272727', textAlign: 'right' }}>
                                כותרת ההטבה
                            </Text>
                        </FieldLabel>
                        <FieldValue>
                            <Text size="sm" weight="normal" style={{ color: '#272727', textAlign: 'right' }}>
                                {title}
                            </Text>
                        </FieldValue>
                    </FieldRow>

                    <FieldRow showBorder={shouldShowBorder(benefitDetailsIndex++, benefitDetailsFields)}>
                        <FieldLabel>
                            <Text size="md" weight="bold" style={{ color: '#272727', textAlign: 'right' }}>
                                תיאור
                            </Text>
                        </FieldLabel>
                        <FieldValue>
                            <Text size="sm" weight="normal" style={{ color: '#272727', textAlign: 'right' }}>
                                {description}
                            </Text>
                        </FieldValue>
                    </FieldRow>

                    {images.length > 0 && (
                        <FieldRow showBorder={shouldShowBorder(benefitDetailsIndex++, benefitDetailsFields)}>
                            <FieldLabel>
                                <Text size="md" weight="bold" style={{ color: '#272727', textAlign: 'right' }}>
                                    תמונות המוצר
                                </Text>
                            </FieldLabel>
                            <ThumbnailsContainer style={{ flexDirection: 'row' }}>
                                {images.slice(0, 4).map((img, index) => (
                                    <ThumbnailImage key={index} src={img} alt={`preview-${index}`} />
                                ))}
                            </ThumbnailsContainer>
                        </FieldRow>
                    )}

                    {price && (
                        <FieldRow showBorder={shouldShowBorder(benefitDetailsIndex++, benefitDetailsFields)}>
                            <FieldLabel>
                                <Text size="md" weight="bold" style={{ color: '#272727', textAlign: 'right' }}>
                                    מחיר המוצר/שירות
                                </Text>
                            </FieldLabel>
                            <PriceRow>
                                {discountNum > 0 && (
                                    <>
                                        <Text size="sm" weight="normal" style={{ color: '#272727', textDecoration: 'line-through' }}>
                                            ₪{originalPrice}
                                        </Text>
                                    </>
                                )}
                                <Text size="sm" weight="normal" style={{ color: '#272727' }}>
                                    | ₪{currentPrice}
                                </Text>

                            </PriceRow>
                        </FieldRow>
                    )}

                    {discountPercentage && (
                        <FieldRow showBorder={shouldShowBorder(benefitDetailsIndex++, benefitDetailsFields)}>
                            <FieldLabel>
                                <Text size="md" weight="bold" style={{ color: '#272727', textAlign: 'right' }}>
                                    אחוזי הנחה
                                </Text>
                            </FieldLabel>
                            <div style={{ display: 'flex', justifyContent: 'flex-start', width: '100%' }}>
                                <DiscountTag>
                                    <Text size="sm" weight="normal" style={{ color: '#272727' }}>
                                        {discountPercentage}%
                                    </Text>
                                </DiscountTag>
                            </div>
                        </FieldRow>
                    )}

                    {selectedCategory && (
                        <FieldRow showBorder={shouldShowBorder(benefitDetailsIndex++, benefitDetailsFields)}>
                            <FieldLabel>
                                <Text size="md" weight="bold" style={{ color: '#272727', textAlign: 'right' }}>
                                    קטגוריה
                                </Text>
                            </FieldLabel>
                            <FieldValue>
                                <Text size="sm" weight="normal" style={{ color: '#272727', textAlign: 'right' }}>
                                    {selectedCategory}
                                </Text>
                            </FieldValue>
                        </FieldRow>
                    )}

                    <FieldRow showBorder={shouldShowBorder(benefitDetailsIndex++, benefitDetailsFields)}>
                        <FieldLabel>
                            <Text size="md" weight="bold" style={{ color: '#272727', textAlign: 'right' }}>
                                סוג קישור להמשך
                            </Text>
                        </FieldLabel>
                        <FieldValue>
                            <Text size="sm" weight="normal" style={{ color: '#272727', textAlign: 'right' }}>
                                {ctaType === 'whatsapp' ? 'WhatsApp' : 'קישור (URL)'}: {ctaValue}
                            </Text>
                        </FieldValue>
                    </FieldRow>

                    {ctaType === 'url' && (
                        <FieldRow showBorder={shouldShowBorder(benefitDetailsIndex++, benefitDetailsFields)}>
                            <FieldLabel>
                                <Text size="md" weight="bold" style={{ color: '#272727', textAlign: 'right' }}>
                                    קוד קופון למימוש
                                </Text>
                            </FieldLabel>
                            <FieldValue>
                                <Text size="sm" weight="normal" style={{ color: '#007EFF', textAlign: 'right' }}>
                                    {couponCode}
                                </Text>
                            </FieldValue>
                        </FieldRow>
                    )}
                </SectionContent>
            </SummarySection>

            {/* Dates Section. A coupon always runs 00:00 to 00:00, so there is no time to show. */}
            {(startDate || endDate) && (
                <SummarySection>
                    <SectionHeader>
                        <SectionTitleRow>
                            <CalendarClockIcon />
                            <Text size="lg" weight="bold" style={{ color: '#272727' }}>
                                תאריכים
                            </Text>
                        </SectionTitleRow>
                        <EditButton onClick={() => onEditStep(2)}>
                            <Text size="sm" weight="normal" style={{ color: '#007EFF' }}>
                                עריכה
                            </Text>
                            <EditIcon />
                        </EditButton>
                    </SectionHeader>

                    <SectionContent>
                        {startDate && (
                            <FieldRow showBorder={shouldShowBorder(datesIndex++, datesFields)}>
                                <FieldLabel>
                                    <Text size="md" weight="bold" style={{ color: '#272727', textAlign: 'right' }}>
                                        בתוקף מתאריך
                                    </Text>
                                </FieldLabel>
                                <FieldValue>
                                    <Text size="sm" weight="normal" style={{ color: '#272727', textAlign: 'right' }}>
                                        {formatDate(startDate)}
                                    </Text>
                                </FieldValue>
                            </FieldRow>
                        )}

                        {endDate && (
                            <FieldRow showBorder={shouldShowBorder(datesIndex++, datesFields)}>
                                <FieldLabel>
                                    <Text size="md" weight="bold" style={{ color: '#272727', textAlign: 'right' }}>
                                        עד תאריך
                                    </Text>
                                </FieldLabel>
                                <FieldValue>
                                    <Text size="sm" weight="normal" style={{ color: '#272727', textAlign: 'right' }}>
                                        {formatDate(endDate)}
                                    </Text>
                                </FieldValue>
                            </FieldRow>
                        )}

                    </SectionContent>
                </SummarySection>
            )}

            {/* Publication Locations Section */}
            {selectedOrganizations.size > 0 && (
                <SummarySection>
                    <SectionHeader>
                        <SectionTitleRow>
                            <Text size="lg" weight="bold" style={{ color: '#272727' }}>
                                ישובים בהם תתפרסם ההטבה
                            </Text>
                            <LocationIcon />
                        </SectionTitleRow>
                        <EditButton onClick={() => onEditStep(3)}>
                            <Text size="sm" weight="normal" style={{ color: '#007EFF' }}>
                                עריכה
                            </Text>
                            <EditIcon />
                        </EditButton>
                    </SectionHeader>

                    <SectionContent>
                        <CommunitiesContainer>
                            {selectedOrgNames.map((name, index) => (
                                <CommunityTag key={index}>
                                    <Text size="md" weight="bold" style={{ color: '#272727' }}>
                                        {name}
                                    </Text>
                                </CommunityTag>
                            ))}
                        </CommunitiesContainer>
                    </SectionContent>
                </SummarySection>
            )}
        </SummaryContainer>
    )
}

export default Summary
